import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { authService } from '../../../shared/services/auth/authService';
import { mapUser } from '../../../utils/userMapper';
import { useQueryClient } from '@tanstack/react-query';

interface AuthContextProps {
  isAuthenticated: boolean;
  user: any;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('auth_user'));
  const [user, setUser] = useState<any>(() => {
    const stored = localStorage.getItem('auth_user');
    return stored ? JSON.parse(stored) : null;
  });

  useEffect(() => {
    const checkAuth = () => {
      const authUser = localStorage.getItem('auth_user');
      setIsAuthenticated(!!authUser);
      setUser(authUser ? JSON.parse(authUser) : null);
    };

    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  const login = async (email: string, password: string) => {
    if (!email || !password) {
      return { success: false, error: 'E-mail e senha são obrigatórios.' };
    }

    try {
      const response = await authService.login(email, password);
      const { user: userData, token } = response.data.data;
      const newUser = mapUser(userData, {});
      setIsAuthenticated(true);
      setUser(newUser);
      localStorage.setItem('auth_user', JSON.stringify(newUser));
      localStorage.setItem('access_token', token);
      return { success: true };
    } catch (error: any) {
      let errorMsg = 'Não foi possível fazer login. Verifique suas credenciais.';
      const backendError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;

      if (backendError === 'Invalid credentials') {
        errorMsg = 'E-mail ou senha incorretos.';
      } else if (backendError === 'Customers are not allowed to login through this endpoint') {
        errorMsg = 'Este tipo de conta não pode acessar a gestão.';
      } else if (typeof backendError === 'string' && backendError !== 'Login failed') {
        errorMsg = backendError;
      }
      return { success: false, error: errorMsg };
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('auth_user');
    localStorage.removeItem('access_token');
   
    queryClient.invalidateQueries({ queryKey: ['events'] });
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}